import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';

const CompanyAcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState<any>(null);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user && mounted) {
          setUser(session.user);
          
          // Check if they already have a company setup
          const { data: companyLink } = await supabase
            .from('company_users')
            .select('company_id')
            .eq('user_id', session.user.id)
            .maybeSingle();

          if (companyLink?.company_id) {
            navigate(`/client/company/dashboard`, { replace: true });
            return;
          }
        } else if (mounted) {
          setError('No invitation session found. Please click the link in your invitation email.');
        }
      } catch (err: any) {
        if (mounted) {
          console.error('Error checking auth:', err);
          setError('An error occurred. Please try again.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: string, session: any) => {
      if (event === 'SIGNED_IN' && session?.user && mounted) {
        setUser(session.user);
        setLoading(true);
        await loadData();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError || !currentUser) {
        throw new Error('You must be logged in. Please click the link in your invitation email.');
      }

      // Update password for the invited user
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.password
      });

      if (updateError) {
        throw updateError;
      }

      // Force full page reload to first-run
      window.location.href = `/company/first-run`;
    } catch (err: any) {
      setError(err.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="ngo-accept-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div className="form-container" style={{ maxWidth: '500px', width: '100%', background: 'white', padding: '2.5rem', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
        <div className="form-section">
          <h1 style={{ fontSize: '1.5rem', color: '#1e293b', marginBottom: '1rem', textAlign: 'center' }}>Set Your Password</h1>
          <p style={{ color: '#64748b', marginBottom: '2rem', textAlign: 'center', fontSize: '0.95rem' }}>
            Welcome! Please set a secure password to activate your company account.
          </p>

          {user && (
            <div style={{
              background: '#f0fdf4',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '2rem',
              border: '1px solid #bbf7d0'
            }}>
              <p style={{ margin: 0, color: '#166534', fontSize: '0.9rem', textAlign: 'center' }}>
                Logged in as <strong>{user.email}</strong>
              </p>
            </div>
          )}

          {error && (
            <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1.5rem', width: '100%' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label style={{ fontWeight: 600, color: '#334155' }}>New Password *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="Minimum 8 characters"
                minLength={8}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', marginTop: '0.5rem' }}
              />
            </div>

            <div className="form-group" style={{ marginTop: '1.5rem' }}>
              <label style={{ fontWeight: 600, color: '#334155' }}>Confirm Password *</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                placeholder="Re-enter password"
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', marginTop: '0.5rem' }}
              />
            </div>

            <div className="form-actions" style={{ marginTop: '2rem' }}>
              <button type="submit" className="btn btn-primary" disabled={loading || !user} style={{ width: '100%', padding: '1rem', fontSize: '1rem' }}>
                {loading ? 'Saving...' : 'Set Password & Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompanyAcceptInvite;
